import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { localizeError } from "@/lib/errorMessages";
import { useMediaQuery } from "@/features/lead/hooks/use-media-query";
import { CustomerForm } from "@/features/customer/components/customer-form";
import { useUpdateCustomer } from "@/features/customer/api";
import type { Customer, CustomerCreateRequest } from "@/types/customer";

interface CustomerEditSheetProps {
  customer: Customer | null;
  onClose: () => void;
  onUpdated?: (customer: Customer) => void;
}

export function CustomerEditSheet({
  customer,
  onClose,
  onUpdated,
}: CustomerEditSheetProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  // Hooks must run unconditionally; we only mount the form when a customer
  // is in scope.
  const id = customer?.id ?? 0;
  const mutation = useUpdateCustomer(id);
  const open = customer !== null;

  if (!open) return null;

  const handleSubmit = async (
    payload: CustomerCreateRequest | Partial<CustomerCreateRequest>,
  ): Promise<Customer> => {
    const updated = await mutation.mutateAsync(payload);
    toast.success("Đã cập nhật khách hàng");
    onUpdated?.(updated);
    onClose();
    return updated;
  };

  const body = (
    <CustomerForm
      mode="edit"
      initial={customer}
      onSubmit={async (payload) => {
        try {
          return await handleSubmit(payload);
        } catch (error) {
          // Surface unknown errors (after the form has already had its shot
          // at mapping VALIDATION_FAILED / CUSTOMER_PHONE_DUPLICATE).
          toast.error(localizeError(error));
          throw error;
        }
      }}
      onCancel={onClose}
      submitLabel="Lưu thay đổi"
    />
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Sửa thông tin khách hàng</DialogTitle>
            <DialogDescription>Cập nhật thông tin liên hệ và ghi chú.</DialogDescription>
          </DialogHeader>
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Sửa thông tin khách hàng</SheetTitle>
          <SheetDescription>Cập nhật thông tin liên hệ và ghi chú.</SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-6">{body}</div>
      </SheetContent>
    </Sheet>
  );
}
